import { useState } from 'react';
import { ArrowDownToLine, ArrowRight, CheckCheck, Pencil, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Media, Status } from '../components/common';
import { useLab } from '../lib/service';
import { ReviewForm } from './ReviewForm';
import { useSeries } from './context';

export function EpisodeRevision() {
  const { revision, setRevision, controlsDisabled, run } = useSeries();
  return (
    <div className="revision-panel">
      <div>
        <Sparkles size={20} />
        <h3>A different take?</h3>
        <p>Describe a broader change. We’ll identify the shots it affects.</p>
      </div>
      <textarea
        aria-label="Episode revision"
        placeholder="The last shot needs to feel more unsettling…"
        maxLength={4000}
        value={revision}
        onChange={(event) => setRevision(event.target.value)}
        rows={2}
      />
      <Button
        variant="outline"
        disabled={controlsDisabled || !revision.trim()}
        onClick={() => void run('REVISE_EPISODE', { instruction: revision.trim() })}
      >
        Plan revision <ArrowRight size={15} />
      </Button>
    </div>
  );
}

/** Review the exact current cut, with a targeted shot change before approval. */
export function EpisodeReview() {
  const { episode: ep, shots, controlsDisabled, setSelectedShot, act, onError } = useSeries();
  const lab = useLab();
  const [choice, setChoice] = useState('');
  const chosenShot = shots.find((shot) => shot._id === choice) ?? shots[0];
  if (!ep?.currentMasterId) return null;
  return (
    <section className="final-review" id="episode-review" tabIndex={-1} aria-label="Episode review">
      <div className="review-preview">
        <Media assetId={ep.currentMasterId} captionsId={ep.currentCaptionsId} video />
        {lab.isDemo && <p className="muted">Simulated footage · no AI charges</p>}
      </div>
      <div className="review-body">
        <span className="eyebrow">THE CURRENT CUT</span>
        <h2>Ready for your eyes.</h2>
        <p>Watch the complete episode, review the performances, and decide if it’s ready.</p>
        <div className="review-facts">
          <span>{shots.length} shots</span>
          <span>{shots.reduce((seconds, shot) => seconds + shot.spec.duration, 0)} seconds</span>
          <span>Screenplay v{ep.scriptVersion}</span>
        </div>
        <Status value={ep.status} />
        <div className="inline-actions">
          <Button
            disabled={controlsDisabled || ep.status === 'ACCEPTED'}
            onClick={() => void act(() => lab.acceptEpisode(ep._id, ep.currentMasterId))}
          >
            <CheckCheck size={16} />
            {ep.status === 'ACCEPTED' ? 'Episode approved' : 'Approve episode'}
          </Button>
          <Button
            variant="outline"
            onClick={() =>
              void lab
                .url(ep.currentMasterId!, true)
                .then((url) => window.open(url, '_blank', 'noopener,noreferrer'))
                .catch(onError)
            }
          >
            <ArrowDownToLine size={16} />
            Export MP4
          </Button>
        </div>
        <p className="muted">
          Approval covers this cut and all {shots.length} current shots. Earlier accepted versions
          remain preserved.
        </p>
        {!!shots.length && (
          <div className="shot-review-picker">
            <h3>Change one shot before approving</h3>
            <p>
              Choose a shot and describe the change. Review its new take, then rebuild and watch the
              updated episode.
            </p>
            <label className="field-label" htmlFor="review-shot">
              Shot to edit
            </label>
            <div className="shot-review-controls">
              <select
                id="review-shot"
                value={chosenShot?._id ?? ''}
                onChange={(event) => setChoice(event.target.value)}
              >
                {shots.map((shot, index) => (
                  <option key={shot._id} value={shot._id}>
                    {String(index + 1).padStart(2, '0')} ·{' '}
                    {shot.spec.action.split('.')[0].slice(0, 90)}
                  </option>
                ))}
              </select>
              <Button
                variant="outline"
                disabled={!chosenShot || controlsDisabled}
                onClick={() => setSelectedShot(chosenShot!._id)}
              >
                <Pencil size={15} />
                Edit selected shot
              </Button>
            </div>
          </div>
        )}
        <details className="screenplay">
          <summary>Revise the episode</summary>
          <EpisodeRevision />
        </details>
        <ReviewForm episodeId={ep._id} onError={onError} />
      </div>
    </section>
  );
}
